import {useRef, useState} from "react";
import {trpc} from "@/app/trpc";
import {useQuery} from "@tanstack/react-query";
import {
  Loader,
  ManagedImage,
  Separator,
  ShadowBox,
  Slider,
  type SliderHandle,
  YStack,
} from "@chayka/ui";
import {BottomSheet} from "../layout";
import {ChevronBack, ChevronForward} from "@chayka/icons";

export const CommonArea = () => {
  const [shownEntry, setShownEntry] = useState<
    (typeof trpc.commonArea.list)["~types"]["output"][number] | null
  >(null);
  const [isOpen, setIsOpen] = useState(false);

  const sliderRef = useRef<SliderHandle | null>(null);

  const {data, isLoading} = useQuery(trpc.commonArea.list.queryOptions({}));

  const handleEntryClick = (entry: typeof shownEntry) => {
    setShownEntry(entry);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setShownEntry(null);
    }, 1000); // Даем время на анимацию закрытия, прежде чем очищать данные
  };

  return (
    <>
      <ShadowBox className="rounded-xl overflow-hidden self-stretch pb-4">
        {isLoading && (
          <YStack className="justify-center items-center h-80">
            <Loader />
          </YStack>
        )}
        <Slider
          withDots
          items={data || []}
          renderItem={({item, isActive}) => (
            <YStack
              key={item.id}
              className="w-full h-80 items-center justify-start relative"
              onClick={() => handleEntryClick(item)}
            >
              <div className="w-full h-80">
                <ManagedImage
                  src={item.previewImage}
                  alt={item.previewHeader}
                  priority={isActive}
                />
              </div>
              <YStack className="gap-1 absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-background w-full py-2 px-4">
                <div className="w-full text-sm font-bold whitespace-nowrap overflow-hidden text-ellipsis">
                  {item.previewHeader}
                </div>
                <div className="w-full text-sm whitespace-nowrap overflow-hidden text-ellipsis line-clamp-1">
                  {item.textMarkdown}
                </div>
              </YStack>
            </YStack>
          )}
        />
      </ShadowBox>
      <BottomSheet fullHeight isOpen={isOpen} onClose={handleClose}>
        <YStack className="gap-4">
          {shownEntry && (
            <>
              <div className="w-full h-64 rounded-xl overflow-hidden relative">
                <Slider
                  ref={sliderRef}
                  items={shownEntry.images}
                  renderItem={({item, isActive}) => (
                    <div className="w-full h-64">
                      <ManagedImage src={item} alt={shownEntry.previewHeader} priority={isActive} />
                    </div>
                  )}
                />
                {shownEntry.images.length > 1 && (
                  <>
                    <div
                      className="absolute top-1/2 transform -translate-y-1/2 left-4 w-8 h-8 text-white cursor-pointer bg-stroke rounded-full flex items-center justify-center"
                      onClick={() => sliderRef.current?.navigate(-1)}
                    >
                      <ChevronBack />
                    </div>
                    <div
                      className="absolute top-1/2 transform -translate-y-1/2 right-4 w-8 h-8 text-white cursor-pointer bg-stroke rounded-full flex items-center justify-center"
                      onClick={() => sliderRef.current?.navigate(1)}
                    >
                      <ChevronForward />
                    </div>
                  </>
                )}
              </div>
              <h2 className="text-xl font-bold">{shownEntry?.previewHeader}</h2>
              <Separator />
              <div className="whitespace-pre-wrap">{shownEntry.textMarkdown}</div>
            </>
          )}
        </YStack>
      </BottomSheet>
    </>
  );
};
